import React, { useState, useEffect } from 'react';
import { Trash2, Eye, Edit, ChevronDown, X } from 'lucide-react';
import apiService from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import AdminLayout from '@/components/layout/AdminLayout';
import CreateNoticeModal from '@/components/modals/CreateNoticeModal';

interface Notice {
  id: string;
  title: string;
  description: string;
  audience: 'PROVIDER' | 'CUSTOMER' | 'INTERNAL';
  priority: 'high' | 'medium' | 'low';
  placement: string;
  scheduled: string;
  status: 'Published' | 'Scheduled' | 'Draft' | 'Archived';
  createdBy: string;
  initials: string;
}

const AdminNoticesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('ALL');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [noticesData, setNoticesData] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  // (audit 2026-06-11) — search input, audience dropdown, sort dropdown
  // and pagination were all inert. State below drives the visible list.
  const [searchText, setSearchText] = useState('');
  const [audienceFilter, setAudienceFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // Fetch notices from database
  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const response = await apiService.request('/admin/notices', 'GET');
        if (response.success && response.data) {
          setNoticesData(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch notices:', error);
        // Show empty state instead of fallback
        setNoticesData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  const handleDeleteNotice = async (noticeId: string) => {
    if (!confirm('Are you sure you want to delete this notice?')) return;
    try {
      const response = await apiService.request(`/admin/notices/${noticeId}`, 'DELETE');
      if (response.success) {
        alert('✓ Notice deleted successfully');
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Delete notice error:', error);
      alert('Failed to delete notice: ' + error.message);
    }
  };

  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [savingNotice, setSavingNotice] = useState(false);

  const handleSaveNotice = async () => {
    if (!editingNotice) return;
    if (!editingNotice.title.trim()) {
      alert('Title is required');
      return;
    }
    setSavingNotice(true);
    try {
      const response = await apiService.request(`/admin/notices/${editingNotice.id}/edit`, 'PATCH', {
        title: editingNotice.title,
        content: editingNotice.description,
      });
      if (response.success) {
        setNoticesData((list) =>
          list.map((n) => (n.id === editingNotice.id ? { ...n, ...editingNotice } : n))
        );
        setEditingNotice(null);
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Edit notice error:', error);
      alert('Failed to edit notice: ' + error.message);
    } finally {
      setSavingNotice(false);
    }
  };

  const tabs = ['ALL', 'CUSTOMER-FACING', 'PROVIDER-FACING', 'INTERNAL', 'SCHEDULED', 'ARCHIVED'];

  const filteredNotices = noticesData
    .filter((n) => {
      const q = searchText.trim().toLowerCase();
      if (!q) return true;
      return (n.title || '').toLowerCase().includes(q) ||
             (n.description || '').toLowerCase().includes(q);
    })
    .filter((n) => audienceFilter === 'all' ? true
      : String(n.audience || '').toLowerCase().includes(audienceFilter))
    .filter((n) => activeTab === 'ALL' ? true
      : String(n.status || '').toUpperCase() === activeTab);

  const sortedNotices = [...filteredNotices].sort((a, b) => {
    if (sortBy === 'title') return String(a.title || '').localeCompare(String(b.title || ''));
    const ta = new Date(a.scheduled || (a as any).created_at || 0).getTime();
    const tb = new Date(b.scheduled || (b as any).created_at || 0).getTime();
    return sortBy === 'oldest' ? ta - tb : tb - ta;
  });

  const totalPages = Math.max(1, Math.ceil(sortedNotices.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, sortedNotices.length);
  const pagedNotices = sortedNotices.slice(pageStart, pageEnd);

  const getAudienceBadgeColor = (audience: string) => {
    switch (audience) {
      case 'PROVIDER':
        return 'bg-blue-100 text-blue-800';
      case 'CUSTOMER':
        return 'bg-green-100 text-green-800';
      case 'INTERNAL':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Published':
        return 'bg-green-100 text-green-800';
      case 'Scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Archived':
        return 'bg-gray-200 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <AdminLayout>
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Notices</h1>
          <p className="text-gray-600 mb-6">Platform notices for customers, providers, and internal use</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-6 mb-6 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-1 py-3 text-sm font-semibold transition-colors relative ${
                activeTab === tab
                  ? 'text-teal-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600"></div>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-xs">
            <input
              type="text"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
              placeholder="Filter by title..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <select
            value={audienceFilter}
            onChange={(e) => { setAudienceFilter(e.target.value); setCurrentPage(1); }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <option value="all">All Audiences</option>
            <option value="provider">Providers</option>
            <option value="customer">Customers</option>
            <option value="admin">Admins</option>
            <option value="public">Public</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="ml-auto flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg"
          >
            <option value="newest">SORT BY: Newest First</option>
            <option value="oldest">SORT BY: Oldest First</option>
            <option value="title">SORT BY: Title</option>
          </select>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="ml-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            + Create Notice
          </button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="py-3 px-4 text-left">
                      <input type="checkbox" className="rounded" />
                    </th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Created By</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedNotices.length === 0 && (
                    <tr><td colSpan={5} className="py-12 text-center text-gray-500">No notices match the current filters.</td></tr>
                  )}
                  {pagedNotices.map((notice) => (
                    <tr key={notice.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <input type="checkbox" className="rounded" />
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-gray-900">{notice.title}</div>
                        <div className="text-xs text-gray-500">{notice.description}</div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={getStatusColor(notice.status)}>
                          {notice.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs text-gray-700 font-semibold">
                            {notice.initials.charAt(0)}
                          </div>
                          <span className="text-sm text-gray-600">{notice.createdBy}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditingNotice(notice)}
                            title="View / Edit"
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            <Eye className="w-4 h-4 text-gray-600" />
                          </button>
                          <button onClick={() => setEditingNotice(notice)} className="p-1 hover:bg-gray-200 rounded transition-colors">
                            <Edit className="w-4 h-4 text-gray-600" />
                          </button>
                          <button onClick={() => handleDeleteNotice(notice.id)} className="p-1 hover:bg-gray-200 rounded transition-colors">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
              <p>
                {sortedNotices.length === 0
                  ? 'NO MATCHING NOTICES'
                  : `SHOWING ${pageStart + 1} TO ${pageEnd} OF ${sortedNotices.length} NOTICES`}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  ←
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 5).map((n) => (
                  <button
                    key={n}
                    onClick={() => setCurrentPage(n)}
                    className={`px-2 py-1 border border-gray-300 rounded ${
                      n === currentPage ? 'bg-teal-600 text-white' : 'hover:bg-gray-50'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  →
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
      <CreateNoticeModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
      {editingNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-lg rounded-lg">
            <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Edit Notice</h2>
              <button onClick={() => setEditingNotice(null)} className="p-1 hover:bg-gray-200 rounded transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">Title</label>
                <input
                  type="text"
                  value={editingNotice.title}
                  onChange={(e) => setEditingNotice({ ...editingNotice, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-gray-600 tracking-wider block mb-2">Description</label>
                <textarea
                  value={editingNotice.description || ''}
                  onChange={(e) => setEditingNotice({ ...editingNotice, description: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setEditingNotice(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotice}
                disabled={savingNotice}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50"
              >
                {savingNotice ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminNoticesPage;
